/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import config from 'config';

/**
 * Types.
 */

export type CryptoBotConfig = {
  id: string;
  enabled: boolean;
  mode: 'dry' | 'live';
  quoteAsset: string;
  baseAssets: string[];
  candleInterval: string;
  strategy: string;
  strategyParams: Record<string, any>;
  riskPerTradePct: number;
  maxOpenPositions: number;
  maxDailyLossPct: number;
  stopLossAtrMult: number;
  takeProfitR: number;
  aiEnabled: boolean;
  hasBinanceKeys: boolean;
  binanceKeyMasked: string | null;
  binanceSource: 'own' | 'cryptogest' | 'cryptomanager' | null;
  binanceImportable: boolean;
  binanceKeysUpdatedAt: string | null;
  sectionsSavedAt: Record<string, string>;
  updatedAt: string | null;
  strategies: string[];
  intervals: string[];
};

/**
 * Payload for updating the config. Binance keys are write-only (never returned)
 * and only sent when the user changes them.
 */

export type CryptoBotConfigUpdate = Partial<CryptoBotConfig> & {
  binanceApiKey?: string;
  binanceApiSecret?: string;
  disconnectBinance?: boolean;
  section?: string;
};

/**
 * Query key.
 */

const KEY = 'cryptoBot';

/**
 * The user's bot configuration.
 */

export function useCryptoBotConfig() {
  const client = useRequestProvider();

  return useQuery<CryptoBotConfig>([KEY, 'config'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotConfig')).then(({ data }: any) => data?.data)
  });
}

/**
 * Update the bot configuration.
 */

export function useUpdateCryptoBotConfig() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CryptoBotConfigUpdate) =>
      client.patch(resolveAppUrl('cryptoBotConfig'), payload).then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Import Binance keys already stored in Crypto Manager / Crypto Gest.
 */

export function useImportBinanceKeys() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('cryptoBotImportKeys')).then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Test the Binance connection with the stored credentials.
 */

export function useTestBinanceConnection() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async () =>
      client.post(resolveAppUrl('cryptoBotTestConnection')).then(({ data }: any) => data?.data) as Promise<{
        ok: boolean;
        error?: string;
        quoteAsset?: string;
        quoteBalance?: number;
        assets?: number;
        balances?: { asset: string; amount: number }[];
      }>
  });
}

/**
 * Overview + positions types.
 */

export type CryptoBotOverview = {
  mode: 'dry' | 'live';
  enabled: boolean;
  cash: number | null;
  binanceBalance: number | null;
  binanceGlobalValue: number | null;
  positionsValue: number;
  equity: number;
  startEquity: number | null;
  totalReturnPct: number | null;
  openPositions: number;
  closedTrades: number;
  wins: number;
  winRatePct: number | null;
  realizedPnl: number;
  pnl: { daily: number; weekly: number; annual: number };
  holdings: Array<{
    asset: string;
    symbol: string;
    qty: number;
    value: number;
    targetValue?: number;
    diffValue?: number;
    diffPct?: number;
  }>;
  usdc: { balance: number; targetValue: number; diffValue: number; diffPct: number } | null;
  rebalanceBand: number | null;
  isRebalance: boolean;
  lastRunAt: string | null;
  timeframe: string;
  equityCurveAssets: string[];
  equityCurve: Array<{ at: string; equity: number; cash: number; breakdown: Record<string, number> }>;
};

/**
 * `BtcCycle` types.
 */

export type BtcCyclePoint = {
  t: number;
  price: number | null;
  fair: number;
  lower: number;
  upper: number;
  ma200w: number | null;
};

export type BtcCycle = {
  series: BtcCyclePoint[];
  halvings: Array<{ label: string; t: number }>;
  phases: Array<{ start: number; end: number; type: 'bull' | 'bear' }>;
  nextHalving: number | null;
  current: {
    t: number;
    price: number;
    phase: 'accumulation' | 'bear' | 'neutral' | 'bull' | 'euphoria';
    isBull: boolean;
    zScore: number;
    drawdownPct: number;
    ma200w: number | null;
    monthsSinceHalving: number | null;
    projectedTop: number | null;
    etaLabel: string;
  };
};

export type CryptoBotPosition = {
  id: string;
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
  strategy: string;
  status: 'open' | 'closed';
  qty: number;
  entryPrice: number;
  entryValue: number;
  stopPrice: number | null;
  takeProfitPrice: number | null;
  exitPrice: number | null;
  realizedPnl: number | null;
  openReason: string | null;
  closeReason: string | null;
  openedAt: string;
  closedAt: string | null;
  currentPrice?: number;
  currentValue?: number;
  unrealizedPnl?: number;
  unrealizedPct?: number | null;
};

/**
 * Activity-log entry.
 */

export type CryptoBotLog = {
  id: number;
  mode: 'dry' | 'live';
  level: 'info' | 'success' | 'warning' | 'error';
  event: string;
  symbol: string | null;
  message: string;
  data: any;
  at: string;
};

/**
 * WebSocket URL for the bot's live log relay, derived from the API base URL.
 */

function botLogStreamUrl(): string {
  const base: string = (config as any)?.api?.baseUrl || '/api';
  const path = '/ws/crypto-bot/logs';

  if (/^https?:\/\//.test(base)) {
    return base.replace(/^http/, 'ws') + path;
  }

  const proto = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = typeof window !== 'undefined' ? window.location.host : '';

  return `${proto}//${host}${base}${path}`;
}

/**
 * Recent activity log. Loaded once over HTTP, then kept live by a WebSocket that
 * pushes new rows as the bot writes them (with a slow HTTP refetch as a fallback
 * if the socket drops).
 */

export function useCryptoBotLogs() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  const query = useQuery<CryptoBotLog[]>([KEY, 'logs'], {
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoBotLogs'), { params: { limit: 500 } }).then(({ data }: any) => data?.data),
    refetchInterval: 30000
  });

  useEffect(() => {
    let closed = false;
    let socket: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) {
        return;
      }

      try {
        socket = new WebSocket(botLogStreamUrl());
      } catch {
        return;
      }

      socket.onopen = () => {
        const current = queryClient.getQueryData<CryptoBotLog[]>([KEY, 'logs']) ?? [];
        const since = current.length ? Math.max(...current.map(entry => entry.id)) : 0;

        socket?.send(JSON.stringify({ since }));
      };

      socket.onmessage = event => {
        try {
          const message = JSON.parse(event.data);

          if (message?.type === 'logs' && Array.isArray(message.logs) && message.logs.length) {
            queryClient.setQueryData<CryptoBotLog[]>([KEY, 'logs'], prev => {
              const seen = new Set((prev ?? []).map(entry => entry.id));
              const fresh = (message.logs as CryptoBotLog[]).filter(entry => !seen.has(entry.id));

              // Server sends ascending; prepend newest-first, cap at server retention.
              return [...fresh.reverse(), ...(prev ?? [])].slice(0, 500);
            });
          }
        } catch {
          // Ignore malformed frames.
        }
      };

      socket.onclose = () => {
        if (!closed && !retry) {
          retry = setTimeout(() => {
            retry = null;
            connect();
          }, 4000);
        }
      };
    };

    connect();

    return () => {
      closed = true;

      if (retry) {
        clearTimeout(retry);
      }

      try {
        socket?.close();
      } catch {
        // Already closing.
      }
    };
  }, [queryClient]);

  return query;
}

/**
 * Reset the dry-run simulation (positions, trades, equity curve).
 */

export function useResetCryptoBotDryRun() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('cryptoBotReset')).then(({ data }: any) => data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries([KEY, 'overview']);
      queryClient.invalidateQueries([KEY, 'positions']);
    }
  });
}

/**
 * `CryptoBotRebalance` type.
 */

export type RebalanceSnapshot = {
  cash: number;
  assets: Record<string, { qty: number; value: number }>;
};

export type CryptoBotRebalance = {
  id: string;
  mode: 'dry' | 'live';
  at: string;
  movedValue: number;
  moves: {
    sold: Array<{ asset: string; qty: number; value: number }>;
    bought: Array<{ asset: string; qty: number; value: number }>;
    before?: RebalanceSnapshot;
    after?: RebalanceSnapshot;
  };
};

/**
 * Rebalance audit log.
 */

export function useCryptoBotRebalances() {
  const client = useRequestProvider();

  return useQuery<CryptoBotRebalance[]>([KEY, 'rebalances'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotRebalances')).then(({ data }: any) => data?.data),
    refetchInterval: 10000
  });
}

/**
 * `ExchangeFilter` type.
 */

export type ExchangeFilter = {
  asset: string;
  symbol: string;
  quote: string;
  stepSize: number | null;
  minQty: number | null;
  minNotional: number | null;
};

/**
 * Deterministic Binance trading filters for the configured pairs.
 */

export function useCryptoBotExchangeFilters() {
  const client = useRequestProvider();

  return useQuery<ExchangeFilter[]>([KEY, 'exchange-filters'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotExchangeFilters')).then(({ data }: any) => data?.data)
  });
}

/**
 * Clear the activity log.
 */

export function useClearCryptoBotLogs() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => client.delete(resolveAppUrl('cryptoBotLogs')).then(({ data }: any) => data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries([KEY, 'logs']);
    }
  });
}

/**
 * Clear the rebalance audit log.
 */

export function useClearCryptoBotRebalances() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => client.delete(resolveAppUrl('cryptoBotRebalances')).then(({ data }: any) => data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries([KEY, 'rebalances']);
    }
  });
}

/**
 * Overview metrics.
 */

export function useCryptoBotOverview(timeframe = '1m') {
  const client = useRequestProvider();

  return useQuery<CryptoBotOverview>([KEY, 'overview', timeframe], {
    queryFn: async () =>
      client.get(resolveAppUrl('cryptoBotOverview'), { params: { timeframe } }).then(({ data }: any) => data?.data)
  });
}

/**
 * BTC market-cycle model (bull/bear) for the overview chart.
 */

export function useCryptoBotBtcCycle() {
  const client = useRequestProvider();

  return useQuery<BtcCycle>([KEY, 'btc-cycle'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotBtcCycle')).then(({ data }: any) => data?.data),
    staleTime: 60 * 60 * 1000
  });
}

/**
 * Open + closed positions.
 */

export function useCryptoBotPositions() {
  const client = useRequestProvider();

  return useQuery<{ open: CryptoBotPosition[]; closed: CryptoBotPosition[] }>([KEY, 'positions'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotPositions')).then(({ data }: any) => data?.data)
  });
}

/**
 * Trigger a run cycle now.
 */

export function useRunCryptoBot() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('cryptoBotRun')).then(({ data }: any) => data?.data),
    onSuccess: () => queryClient.invalidateQueries([KEY])
  });
}

/**
 * Backtest a strategy for a pair (paper only).
 */

export function useBacktestCryptoBot() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async (payload: { baseAsset?: string; strategy?: string; candleInterval?: string }) =>
      client.post(resolveAppUrl('cryptoBotBacktest'), payload).then(({ data }: any) => data?.data)
  });
}

/**
 * Backtest data availability + job types.
 */

export type BacktestDataPair = {
  baseAsset: string;
  symbol: string;
  hasData: boolean;
  rows: number | null;
  firstAt: number | null;
  lastAt: number | null;
  updatedAt: string | null;
};

export type BacktestData = {
  interval: string;
  quoteAsset: string;
  years: number;
  strategy: string;
  pairs: BacktestDataPair[];
  missing: string[];
  ready: boolean;
};

export type DownloadItem = {
  baseAsset: string;
  symbol: string;
  status: 'pending' | 'downloading' | 'done' | 'error';
  monthsDone: number;
  monthsActive: number;
  monthsTotal: number;
  candles: number;
  error?: string;
};

export type DownloadJob = {
  running: boolean;
  done: boolean;
  cancelled: boolean;
  error: string | null;
  interval: string;
  years: number;
  items: DownloadItem[];
  startedAt: string;
};

export type BacktestJob = {
  running: boolean;
  done: boolean;
  cancelled: boolean;
  phase: 'loading' | 'computing';
  error: string | null;
  symbol: string;
  strategy: string;
  interval: string;
  candlesTotal: number;
  candlesDone: number;
  progress: number;
  result: any;
  startedAt: string;
};

/**
 * Which configured pairs have local candle files at the configured interval.
 */

export function useCryptoBotBacktestData() {
  const client = useRequestProvider();

  return useQuery<BacktestData>([KEY, 'backtest-data'], {
    queryFn: async () => client.get(resolveAppUrl('cryptoBotBacktestData')).then(({ data }: any) => data?.data)
  });
}

/**
 * Start downloading history for missing (or given) pairs.
 */

export function useStartBacktestDownload() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { baseAssets?: string[] } = {}) =>
      client.post(resolveAppUrl('cryptoBotBacktestDataDownload'), payload).then(({ data }: any) => data?.data),
    // Seed the status cache with the fresh job so the modal shows the new pairs
    // immediately instead of the previous job until the next poll.
    onSuccess: job => queryClient.setQueryData([KEY, 'backtest-download-status'], job)
  });
}

/**
 * Downloaded files for one base asset (across intervals).
 */

export type PairDataset = {
  interval: string;
  sizeBytes: number;
  rows: number;
  firstAt: number | null;
  lastAt: number | null;
  updatedAt: string | null;
};

export function useCryptoBotPairFiles(baseAsset: string | null) {
  const client = useRequestProvider();

  return useQuery<{ symbol: string; files: PairDataset[] }>([KEY, 'pair-files', baseAsset], {
    enabled: !!baseAsset,
    queryFn: async () =>
      client
        .get(resolveAppUrl('cryptoBotBacktestDataFiles'), { params: { baseAsset } })
        .then(({ data }: any) => data?.data)
  });
}

/**
 * Cancel the running download job.
 */

export function useCancelBacktestDownload() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async () =>
      client.post(resolveAppUrl('cryptoBotBacktestDataCancel')).then(({ data }: any) => data?.data)
  });
}

/**
 * Poll download job progress while it runs.
 */

export function useBacktestDownloadStatus(enabled: boolean) {
  const client = useRequestProvider();

  return useQuery<DownloadJob | null>([KEY, 'backtest-download-status'], {
    enabled,
    refetchInterval: data => (data?.running ? 1000 : false),
    queryFn: async () => client.get(resolveAppUrl('cryptoBotBacktestDataStatus')).then(({ data }: any) => data?.data)
  });
}

/**
 * Start a file-based backtest run (background).
 */

export function useStartBacktestRun() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      baseAsset?: string;
      strategy?: string;
      startEquity?: number;
      from?: string;
      to?: string;
    }) => client.post(resolveAppUrl('cryptoBotBacktestRun'), payload).then(({ data }: any) => data?.data),
    // Seed the status cache with the fresh job so the modal shows the new run
    // immediately (and restarts polling) instead of the previous result.
    onSuccess: job => queryClient.setQueryData([KEY, 'backtest-run-status'], job)
  });
}

/**
 * Cancel the running backtest job.
 */

export function useCancelBacktestRun() {
  const client = useRequestProvider();

  return useMutation({
    mutationFn: async () => client.post(resolveAppUrl('cryptoBotBacktestCancel')).then(({ data }: any) => data?.data)
  });
}

/**
 * Poll backtest job progress while it runs.
 */

export function useBacktestRunStatus(enabled: boolean) {
  const client = useRequestProvider();

  return useQuery<BacktestJob | null>([KEY, 'backtest-run-status'], {
    enabled,
    refetchInterval: data => (data?.running ? 1000 : false),
    queryFn: async () => client.get(resolveAppUrl('cryptoBotBacktestStatus')).then(({ data }: any) => data?.data)
  });
}
